import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Chip, IconButton, Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import TableChartIcon from '@mui/icons-material/TableChart'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

Index.layout = (page) => <AppShell>{page}</AppShell>

const CATEGORY_LABELS = { residential: 'Wohnung', commercial: 'Gewerbe', parking: 'Stellplatz', other: 'Sonstiges' }

export default function Index({ property, units, attribute_types, filters = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [q, setQ] = useState(filters.q || '')

  const applyFilters = () => {
    router.get(`/m/${tenant.slug}/properties/${property.id}/units`, { q: q || undefined },
      { preserveState: true, replace: true })
  }

  // MEA totals
  const meaType = attribute_types.find((at) => at.slug === 'mea')
  const meaTotal = units.reduce((sum, u) => {
    const v = u.attributes?.find((a) => a.slug === 'mea')?.value
    return sum + (parseFloat(v) || 0)
  }, 0)

  return (
    <Box>
      <PageHeader
        title="Einheiten"
        breadcrumbs={[
          { label: 'Einheiten' },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/attributes`)}
            >
              Attribute bearbeiten
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/new`)}
              data-testid="new-unit-btn"
            >
              Neue Einheit
            </Button>
          </Box>
        }
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Suche nach Nummer, Lage..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          sx={{ minWidth: 240 }}
        />
        <Button variant="outlined" size="small" onClick={applyFilters}>Filtern</Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Nr.</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kategorie</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Lage</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Gebäude</TableCell>
              {attribute_types.filter((at) => ['mea', 'living_area', 'heating_area'].includes(at.slug)).map((at) => (
                <TableCell key={at.id} sx={{ fontWeight: 600 }}>
                  {at.name} {at.unit_of_measure ? `(${at.unit_of_measure})` : ''}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600 }}>Eigentümer / Mieter</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {units.length === 0 && (
              <TableRow>
                <TableCell colSpan={99} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Einheiten gefunden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {units.map((unit) => (
              <TableRow
                key={unit.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/${unit.id}`)}
                data-testid={`unit-row-${unit.id}`}
              >
                <TableCell><Typography variant="body2" fontWeight={500}>{unit.number}</Typography></TableCell>
                <TableCell>
                  <Chip size="small" label={CATEGORY_LABELS[unit.category] || unit.category}
                    variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                </TableCell>
                <TableCell>{unit.location}</TableCell>
                <TableCell>{unit.building_name}</TableCell>
                {attribute_types.filter((at) => ['mea', 'living_area', 'heating_area'].includes(at.slug)).map((at) => (
                  <TableCell key={at.id}>
                    {unit.attributes?.find((a) => a.slug === at.slug)?.value}
                  </TableCell>
                ))}
                <TableCell>{unit.current_contact_name}</TableCell>
                <TableCell>
                  <Chip size="small" label={unit.active ? 'Aktiv' : 'Inaktiv'}
                    color={unit.active ? 'success' : 'default'} variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }} />
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small"
                      onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/${unit.id}/edit`)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {/* Totals row */}
            {units.length > 0 && meaType && (
              <TableRow sx={{ backgroundColor: 'background.subtle', fontWeight: 600 }}>
                <TableCell colSpan={4}><Typography variant="body2" fontWeight={600}>Summe</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color={meaTotal === 1000 ? 'success.main' : 'warning.main'}>
                    {meaTotal} {meaTotal !== 1000 && '⚠'}
                  </Typography>
                </TableCell>
                <TableCell colSpan={99} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
